import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Send, Users, ShoppingBag, Store, AlertTriangle, Wrench,
  Megaphone, Sparkles, Clock, CheckCircle, XCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BROADCAST_TYPES = [
  { id: 'announcement', label: 'Announcement', icon: Megaphone, color: '#00F0FF' },
  { id: 'maintenance', label: 'Maintenance', icon: Wrench, color: '#FFA500' },
  { id: 'emergency', label: 'Emergency', icon: AlertTriangle, color: '#FF4444' },
  { id: 'update', label: 'Platform Update', icon: Sparkles, color: '#00FF94' }
];

const AUDIENCES = [
  { id: 'all', label: 'All Users', icon: Users },
  { id: 'customers', label: 'Customers Only', icon: ShoppingBag },
  { id: 'sellers', label: 'Sellers Only', icon: Store }
];

function BroadcastAdmin() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [broadcastType, setBroadcastType] = useState('announcement');
  const [audience, setAudience] = useState('all');
  const [sending, setSending] = useState(false);
  const [userCounts, setUserCounts] = useState({ all: 0, customers: 0, sellers: 0 });
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    loadUserCounts();
    loadHistory();
  }, []);

  const loadUserCounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/user-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserCounts(res.data);
    } catch (error) {
      console.error('Failed to load user counts:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/broadcast/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.broadcasts || []);
    } catch (error) {
      console.error('Failed to load broadcast history:', error);
    }
  };

  const sendBroadcast = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Please enter subject and message');
      return;
    }

    const recipientCount = userCounts[audience] || 0;
    if (!window.confirm(`Send this ${broadcastType} to ${recipientCount} ${audience} users?`)) {
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/admin/broadcast`,
        {
          subject,
          message,
          broadcast_type: broadcastType,
          audience
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setSubject('');
      setMessage('');
      loadHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const selectedType = BROADCAST_TYPES.find(t => t.id === broadcastType);

  return (
    <div className="space-y-4 md:space-y-6 overflow-hidden">
      <div className="flex items-center justify-between">
        <h3 className="text-lg md:text-xl font-bold flex items-center gap-2">
          <Megaphone className="w-5 h-5 md:w-6 md:h-6 text-[#00F0FF]" />
          Broadcast Center
        </h3>
      </div>

      {/* Audience Selection with Counts */}
      <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
        <div className="flex md:grid md:grid-cols-3 gap-3 md:gap-4 min-w-max md:min-w-0">
          {AUDIENCES.map((aud) => {
            const Icon = aud.icon;
            const count = userCounts[aud.id] || 0;
            return (
              <button
                key={aud.id}
                onClick={() => setAudience(aud.id)}
                className={`p-3 md:p-4 rounded-xl border transition-all w-[110px] md:w-auto flex-shrink-0 md:flex-shrink ${
                  audience === aud.id
                    ? 'border-[#00F0FF] bg-[#00F0FF]/10'
                    : 'border-white/10 hover:border-white/20'
                }`}
              >
                <Icon className={`w-5 h-5 md:w-6 md:h-6 mb-1 md:mb-2 ${audience === aud.id ? 'text-[#00F0FF]' : 'text-neutral-400'}`} />
                <p className="font-medium text-sm md:text-base">{aud.label}</p>
                <p className="text-xl md:text-2xl font-bold mt-1">{count}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Broadcast Type Selection */}
      <div>
        <label className="text-xs md:text-sm text-neutral-400 mb-2 block">Broadcast Type</label>
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
          <div className="flex gap-2 min-w-max md:min-w-0 md:flex-wrap">
            {BROADCAST_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => setBroadcastType(type.id)}
                  className={`flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 rounded-lg border transition-all whitespace-nowrap text-sm ${
                    broadcastType === type.id
                      ? 'border-current bg-current/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                  style={{ 
                    borderColor: broadcastType === type.id ? type.color : undefined,
                    color: broadcastType === type.id ? type.color : undefined
                  }}
                >
                  <Icon className="w-4 h-4" />
                  {type.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Message Composition */}
      <div className="space-y-4">
        <div>
          <label className="text-sm text-neutral-400 mb-2 block">Subject</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter broadcast subject..."
            className="bg-neutral-800 border-white/10"
          />
        </div>
        <div>
          <label className="text-sm text-neutral-400 mb-2 block">Message</label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your message to users..."
            className="bg-neutral-800 border-white/10 min-h-[120px]"
            rows={5}
          />
        </div>
      </div>

      {/* Preview & Send */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 md:p-4 bg-neutral-800/50 rounded-xl">
        <div className="flex items-center gap-2 md:gap-3 flex-wrap">
          {selectedType && (
            <div 
              className="px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium"
              style={{ backgroundColor: `${selectedType.color}20`, color: selectedType.color }}
            >
              {selectedType.label}
            </div>
          )}
          <span className="text-neutral-400 text-xs md:text-sm">
            → {userCounts[audience] || 0} users
          </span>
        </div>
        <Button
          onClick={sendBroadcast}
          disabled={sending || !subject.trim() || !message.trim()}
          size="sm"
          className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-bold px-4 md:px-6 whitespace-nowrap"
        >
          {sending ? (
            <span className="text-sm">Sending...</span>
          ) : (
            <>
              <Send className="w-4 h-4 mr-1 md:mr-2" />
              <span className="text-sm">Send</span>
            </>
          )}
        </Button>
      </div>

      {/* Broadcast History */}
      <div className="border border-white/10 rounded-xl overflow-hidden">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-neutral-400" />
            <span className="font-medium">Broadcast History</span>
            <span className="text-neutral-400 text-sm">({history.length})</span>
          </div>
          {showHistory ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {showHistory && (
          <div className="border-t border-white/10 max-h-[300px] overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-neutral-400 text-center py-8">No broadcasts sent yet</p>
            ) : (
              history.map((broadcast) => {
                const type = BROADCAST_TYPES.find(t => t.id === broadcast.broadcast_type);
                return (
                  <div key={broadcast.broadcast_id} className="p-4 border-b border-white/5 last:border-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {type && (
                            <span 
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: `${type.color}20`, color: type.color }}
                            >
                              {type.label}
                            </span>
                          )}
                          <span className="text-xs text-neutral-400 capitalize">{broadcast.audience}</span>
                        </div>
                        <p className="font-medium">{broadcast.subject}</p>
                        <p className="text-sm text-neutral-400 mt-1 line-clamp-1">{broadcast.message}</p>
                      </div>
                      <div className="text-right text-sm">
                        <div className="flex items-center gap-1 text-green-400">
                          <CheckCircle className="w-3 h-3" />
                          {broadcast.sent_count}
                        </div>
                        {broadcast.failed_count > 0 && (
                          <div className="flex items-center gap-1 text-red-400">
                            <XCircle className="w-3 h-3" />
                            {broadcast.failed_count}
                          </div>
                        )}
                        <p className="text-neutral-500 text-xs mt-1">{formatDate(broadcast.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BroadcastAdmin;
